import { NetworkStatus, useQuery } from "@apollo/client";
import { useMemo } from "react";

import { SearchTofuWorkspacesOutput } from "types/generated";

import useTofuWorkspacesFiltersVariables from "../useSearchTofuWorkspacesSuggestions/useFiltersVariables";
import { SEARCH_TOFU_WORKSPACES } from "./gql";
import useTofuWorkspacesStore from "./store";

type UseSearchTofuWorkspacesProps = {
  stackId: string;
};

const useSearchTofuWorkspaces = ({ stackId }: UseSearchTofuWorkspacesProps) => {
  const { cursor, rowsPerPage, requestedPage } = useTofuWorkspacesStore();
  const { sortOptionFields, fullTextSearch } = useTofuWorkspacesFiltersVariables();

  const { data, error, networkStatus, previousData } = useQuery<{
    searchTofuWorkspaces: SearchTofuWorkspacesOutput;
  }>(SEARCH_TOFU_WORKSPACES, {
    variables: {
      input: {
        first: rowsPerPage,
        after: cursor,
        requestedPage,
        orderBy: sortOptionFields,
        fullTextSearch,
        predicates: [
          {
            field: "stack",
            constraint: {
              stringMatches: [stackId],
            },
          },
        ],
      },
    },
  });

  const workspaces = useMemo(() => {
    return (
      data?.searchTofuWorkspaces.edges.map((edge) => edge.node) ||
      previousData?.searchTofuWorkspaces.edges.map((edge) => edge.node) ||
      []
    );
  }, [data, previousData]);

  const initialLoading = workspaces.length === 0 && networkStatus === NetworkStatus.loading;
  const hasSearchQuery = !!fullTextSearch;
  const isEmpty = !workspaces.length && !initialLoading && !hasSearchQuery;
  const searchIsEmpty = !workspaces.length && !initialLoading && hasSearchQuery;

  return {
    workspaces,
    error,
    initialLoading,
    isEmpty,
    searchIsEmpty,
    startCursor: data?.searchTofuWorkspaces?.pageInfo.startCursor,
    endCursor: data?.searchTofuWorkspaces?.pageInfo.endCursor,
  };
};

export default useSearchTofuWorkspaces;
